'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { ImagePlus, Loader2, Upload, X } from 'lucide-react';
import { getBackendUrl } from '@/lib/env';
import { cn } from '@/lib/utils';

export type ImageFileUploadProps = {
  label: string;
  hint?: string;
  accept?: string;
  currentUrl?: string | null;
  placeholderIcon?: React.ReactNode;
  onFileSelect: (file: File | null) => void;
  validate?: (file: File) => string | null;
  uploadProgress?: number | null;
  disabled?: boolean;
  /** Empty drop-zone frame. Prefer fixed heights for compact placeholders. */
  frameClass?: string;
  /** @deprecated Use frameClass */
  aspectClass?: string;
  /** How the uploaded image is displayed inside the preview. */
  objectFit?: 'cover' | 'contain';
};

export function ImageFileUpload({
  label,
  hint,
  accept = 'image/png,image/jpeg,image/jpg,image/webp',
  currentUrl,
  placeholderIcon,
  onFileSelect,
  validate,
  uploadProgress = null,
  disabled = false,
  frameClass,
  aspectClass = 'h-[140px] w-full',
  objectFit = 'contain',
}: ImageFileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(currentUrl ?? null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [imageReady, setImageReady] = useState(false);
  const objectUrlRef = useRef<string | null>(null);

  const emptyFrameClass = frameClass ?? aspectClass;

  useEffect(() => {
    setImageReady(false);

    if (!currentUrl) {
      if (!objectUrlRef.current) {
        setPreview(null);
      }
      return;
    }
    if (currentUrl.startsWith('blob:')) {
      setPreview(currentUrl);
      return;
    }

    let cancelled = false;
    const tester = new Image();
    tester.onload = () => {
      if (cancelled) return;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      setPreview(currentUrl);
      setImageReady(true);
    };
    tester.onerror = () => {
      if (cancelled) return;

      // If local loopback URL failed, retry against the active backend origin.
      if (
        (currentUrl.includes('127.0.0.1:8000') || currentUrl.includes('localhost:8000'))
      ) {
        const backend = getBackendUrl().replace(/\/$/, '');
        if (!backend || backend.includes('127.0.0.1') || backend.includes('localhost')) {
          if (!objectUrlRef.current) {
            setPreview(null);
            setImageReady(false);
          }
          return;
        }
        const prodFallback = currentUrl.replace(
          /http:\/\/(127\.0\.0\.1|localhost):8000/,
          backend,
        );
        const fallbackTester = new Image();
        fallbackTester.onload = () => {
          if (cancelled) return;
          if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
          }
          setPreview(prodFallback);
          setImageReady(true);
        };
        fallbackTester.onerror = () => {
          if (cancelled) return;
          if (!objectUrlRef.current) {
            setPreview(null);
            setImageReady(false);
          }
        };
        fallbackTester.src = prodFallback;
        return;
      }

      // Do not set preview to a broken URL (prevents second 404 from <img src>)
      if (!objectUrlRef.current) {
        setPreview(null);
        setImageReady(false);
      }
    };
    tester.src = currentUrl;
    return () => {
      cancelled = true;
    };
  }, [currentUrl]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, []);

  const applyFile = useCallback(
    (file: File | null) => {
      setError(null);
      setImageReady(false);
      if (!file) {
        onFileSelect(null);
        return;
      }
      const validationError = validate?.(file);
      if (validationError) {
        setError(validationError);
        return;
      }
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
      const url = URL.createObjectURL(file);
      objectUrlRef.current = url;
      setPreview(url);
      onFileSelect(file);
    },
    [onFileSelect, validate],
  );

  const clearPreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setPreview(null);
    setImageReady(false);
    if (inputRef.current) inputRef.current.value = '';
    onFileSelect(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    applyFile(e.target.files?.[0] ?? null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    applyFile(e.dataTransfer.files?.[0] ?? null);
  };

  const showPreview = Boolean(preview);

  return (
    <div className="flex flex-col gap-2">
      <div>
        <label className="block text-sm font-medium text-slate-700">{label}</label>
        {hint && <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{hint}</p>}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        disabled={disabled}
        onChange={handleInputChange}
      />
      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        className={cn(
          'relative isolate overflow-hidden rounded-xl border-2 border-dashed cursor-pointer transition-colors',
          showPreview
            ? 'inline-block h-auto w-fit max-w-full bg-bg'
            : cn('w-full', emptyFrameClass),
          isDragging
            ? 'border-primary bg-primary/5'
            : showPreview
              ? 'border-neutral-200'
              : 'border-nature-border bg-neutral-50',
          disabled && 'opacity-60 cursor-not-allowed',
        )}
      >
        {showPreview ? (
          <div className={cn('relative', !imageReady && 'min-h-[96px] min-w-[96px]')}>
            {!imageReady && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              </div>
            )}
            <img
              src={preview!}
              alt={`${label} preview`}
              onLoad={() => {
                setImageReady(true);
                setError(null);
              }}
              onError={() => {
                setImageReady(false);
                setError('Could not load image preview. Try uploading again.');
              }}
              className={cn(
                'block max-h-[280px] max-w-full rounded-lg',
                objectFit === 'contain' ? 'h-auto w-auto' : 'h-auto w-full max-w-[520px] object-cover',
                imageReady ? 'opacity-100' : 'opacity-0',
              )}
              draggable={false}
            />
          </div>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 p-3 text-slate-500">
            {placeholderIcon ?? <ImagePlus className="h-6 w-6 shrink-0" />}
            <span className="max-w-full px-1 text-center text-xs font-medium leading-snug sm:text-sm">
              Drag & drop or click to upload
            </span>
          </div>
        )}
        {uploadProgress !== null && uploadProgress < 100 && (
          <div className="absolute inset-x-0 bottom-0 z-20 flex items-center gap-2 bg-black/60 px-3 py-2 text-xs text-text-inverse">
            <Loader2 className="h-3 w-3 animate-spin" />
            Uploading {uploadProgress}%
          </div>
        )}
        {showPreview && !disabled && (
          <>
            <button
              type="button"
              className="absolute right-2 top-2 z-20 rounded-full bg-bg/95 p-1.5 shadow-md ring-1 ring-black/5 hover:bg-bg"
              onClick={clearPreview}
              aria-label={`Remove ${label}`}
            >
              <X className="h-4 w-4 text-slate-700" />
            </button>
            {uploadProgress === null && (
              <div className="absolute bottom-2 left-2 z-20 inline-flex items-center gap-1 rounded-lg bg-bg/95 px-2.5 py-1 text-xs font-medium text-slate-700 shadow-md ring-1 ring-black/5">
                <Upload className="h-3 w-3" /> Change
              </div>
            )}
          </>
        )}
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}
